import { Button, Space, Table, TableProps } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  LoadingOutlined,
  SyncOutlined
} from "@ant-design/icons";
import { GetReconciliationGroupsApiParams, JOB_STATUS, ReconciliationGroup } from "types/reconciliation";
import { useGetReconciliationGroupsQuery } from "../../../redux/api/reconciliationsApiSlice";
import React, { Key, useMemo } from "react";
import BrokerFilter from "../../tables/negotiationsTables/BrokerFilter";
import { getColumnDateProps, getStandardFilter } from "../../tables/negotiationsTables/columnsUtils";
import { useReconciliationGroupsTable } from "../../../context/ReconciliationGroupsTableContext";
import { isEmpty } from "lodash";
import { ERROR_COLOR, printValue } from "utils/reconciliationTableUtils";
import { StartGroupReconcilationButton } from "./StartGroupReconcilationButton";
import { DownloadReconciliationGroupFilesButton } from "./DownloadReconciliationGroupFilesButton";

export const ReconciliationGroupTable = ({ defaultFilters, tableProps, onRecordDetailClick }: {
  defaultFilters?: GetReconciliationGroupsApiParams,
  tableProps?: TableProps<ReconciliationGroup>,
  onRecordDetailClick?: (record: ReconciliationGroup) => void,
}) => {

  const {
    page,
    pageSize,
    broker,
    ecPeriod,
    reconciliationJobStatus,
      roreJobStatus,
    setPage,
    setPageSize,
    setBroker,
    setOrdering,
    setEcPeriod,
    setUnreconciledCredit,
    getParamsForApiRequest,
    setReconciliationJobStatus,
      setRoreJobStatus,
  } = useReconciliationGroupsTable();

  const {
    data: reconciliationGroup,
    isLoading,
    isFetching,
    refetch
  } = useGetReconciliationGroupsQuery(getParamsForApiRequest(defaultFilters), { pollingInterval: 120000 });

  const cols: TableProps<ReconciliationGroup>["columns"] = useMemo(() => ([
      {
        key: "rore_job_status",
        dataIndex: "rore_job_status",
        title: "Stato lettura EC",
        filteredValue: roreJobStatus,
        ...getStandardFilter([{ text: "In elaborazione", value: "P" }, {
          text: "Completato",
          value: "D"
        }, { text: "In errore", value: "E" }]),
        width: 80,
        ellipsis: true,
        onCell: () => ({ style: { textAlign: "center" } }),
        render: (value) => {
          switch (value) {
            case JOB_STATUS.PENDING:
              return <SyncOutlined spin style={{ color: "#1677ff" }} />;
            case JOB_STATUS.DONE:
              return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
            case JOB_STATUS.ERROR:
              return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
            default:
              return <></>;
          }
        }
      },
      {
        key: "reconciliation_job_status",
        dataIndex: "reconciliation_job_status",
        title: "Stato riconciliazione",
        filteredValue: reconciliationJobStatus,
        ...getStandardFilter([{ text: "In elaborazione", value: "P" }, {
          text: "Completato",
          value: "D"
        }, { text: "In errore", value: "E" }]),
        width: 80,
        ellipsis: true,
        onCell: () => ({ style: { textAlign: "center" } }),
        render: (value) => {
          switch (value) {
            case JOB_STATUS.PENDING:
              return <SyncOutlined spin style={{ color: "#1677ff" }} />;
            case JOB_STATUS.DONE:
              return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
            case JOB_STATUS.ERROR:
              return <CloseCircleOutlined style={{ color: "#f5222d" }} />;
            default:
              return <></>;
          }
        }
      }, {
        key: "broker",
        dataIndex: "broker_name",
        title: "Broker",
        filteredValue: broker,
        filterDropdown: (props) => BrokerFilter({ ...props, isReconciliationBroker: true }),
        sorter: { multiple: 1 },
        ellipsis: true,
        render: (value) => printValue(value, "text")
      }, {
        key: "ec_period",
        dataIndex: "ec_period",
        title: "Data ultimo EC",
        sorter: { multiple: 2 },
        filteredValue: ecPeriod,
        ...getColumnDateProps("ec_period", "month"),
        ellipsis: true,
        onCell: (record) => ({
          style: { ...(!record.ec_period && { background: ERROR_COLOR }) }
        }),
        render: (value) => printValue(value, "date", { dateTemplate: "MMMM YYYY" })
      }, /*{
        key: "unreconciled_credit",
        dataIndex: "unreconciled_credit",
        title: "unreconciled_credit",
        className: "justify-right",
        filteredValue: unreconciledCredit,
        ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
        sorter: { multiple: 3 },
        ellipsis: true,
        render: (value) => printValue(value, "currency")
      }, {
        key: "unreconciled_debit",
        dataIndex: "unreconciled_debit",
        title: "unreconciled_debit",
        className: "justify-right",
        filteredValue: unreconciledDebit,
        ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
        sorter: { multiple: 4 },
        ellipsis: true,
        render: (value) => printValue(value, "currency")
      },*/ {
        title: "Azioni",
        key: "actions",
        fixed: "right",
        onCell: () => ({ style: { justifyContent: "center", alignItems: "center", display: "flex" } }),
        render: (_, record) => {
          const disabled = record.rore_job_status === JOB_STATUS.PENDING;
          return (
            <Space>
              {onRecordDetailClick &&
                <Button
                  title={"Vedi estratti conto"}
                  type={"text"}
                  icon={<EyeOutlined />}
                  onClick={() => {
                    onRecordDetailClick(record);
                  }}
                  disabled={disabled}
                />}
              {record.reconciliation_job_status !== JOB_STATUS.PENDING &&
                <StartGroupReconcilationButton
                  uuid={record.uuid}
                  onStartSuccessful={refetch} disabled={disabled} />}
              <DownloadReconciliationGroupFilesButton reconciliationGroup={record} disabled={disabled}/>
            </Space>
          );
        }
      }
    ]),
    [broker, ecPeriod, onRecordDetailClick, reconciliationJobStatus, refetch, roreJobStatus]);

  return (
    <Table<ReconciliationGroup>
      loading={{ indicator: <LoadingOutlined />, spinning: isLoading || isFetching, delay: 0 }}
      rowKey={"uuid"}
      columns={cols}
      scroll={{ x: true }}
      dataSource={reconciliationGroup?.results}
      pagination={{
        current: page,
        pageSize: pageSize,
        showSizeChanger: true,
        total: reconciliationGroup?.count ?? 0,
        showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} titoli`,
        onChange: (page, pageSize) => {
          setPage(page);
          setPageSize(pageSize);
        }
      }}
      onChange={(_, filters, sorter) => {
        // Gestione ordinamento
        const buildOrderingString = (sorterValue: any): string => {
          if (Array.isArray(sorterValue)) {
            return sorterValue
              .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
              .join(",");
          } else if (sorterValue.order) {
            return sorterValue.order === "descend" ? `-${sorterValue.field}` : `${sorterValue.field}`;
          }
          return "";
        };

        // Applicazione ordinamento
        setOrdering(buildOrderingString(sorter));

        // Gestione filtri
        if (filters) {
          // Mappa delle funzioni setter per ogni tipo di filtro
          const filterSetters: Record<string, (value: Key[]) => void> = {
            broker: setBroker,
            ec_period: setEcPeriod,
            unreconciled_credit: setUnreconciledCredit,
            reconciliation_job_status: setReconciliationJobStatus,
            rore_job_status: setRoreJobStatus,
          };

          // Applica i setter per ogni filtro
          Object.entries(filterSetters).forEach(([filterName, setter]) => {
            const filterValue = filters[filterName];
            setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
          });
        }
      }}
      {...tableProps}
    />
  );
};